import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export default function CalendarView({ events, config, loading }) {
  const [currentDate, setCurrentDate] = useState(new Date());

  // Debug: ver qué eventos recibe el componente
  React.useEffect(() => {
    console.log('CalendarView - eventos recibidos:', events);
    console.log('CalendarView - total eventos:', events.length);
    if (events.length > 0) {
      console.log('CalendarView - primer evento:', events[0]);
      console.log('CalendarView - campos del evento:', Object.keys(events[0]));
    }
  }, [events]);

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    const startDay = firstDay.getDay();

    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const getEventForDay = (date) => {
    if (!date) return null;
    const dateStr = date.toISOString().split('T')[0];
    const event = events.find(e => {
      // Comparar solo la parte de fecha (sin hora)
      const eventDate = e.fecha.split('T')[0];
      return eventDate === dateStr;
    });
    if (event) {
      console.log('Evento encontrado para', dateStr, ':', event);
    }
    return event;
  };

  const isWorkingDay = (date) => {
    if (!date || !config) return true;
    const dayOfWeek = date.getDay();
    const workDays = [
      config.trabaja_domingo,
      config.trabaja_lunes,
      config.trabaja_martes,
      config.trabaja_miercoles,
      config.trabaja_jueves,
      config.trabaja_viernes,
      config.trabaja_sabado
    ];
    return workDays[dayOfWeek];
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const days = getDaysInMonth(currentDate);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200">
        <button onClick={previousMonth} className="p-2 hover:bg-white rounded-lg transition-colors">
          <ChevronLeft className="w-5 h-5 text-gray-700" />
        </button>
        <h3 className="text-2xl font-bold text-gray-800">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h3>
        <button onClick={nextMonth} className="p-2 hover:bg-white rounded-lg transition-colors">
          <ChevronRight className="w-5 h-5 text-gray-700" />
        </button>
      </div>

      {/* Grid */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Day names */}
        <div className="grid grid-cols-7 bg-gray-100 border-b border-gray-200">
          {dayNames.map((day) => (
            <div key={day} className="text-center py-3 font-semibold text-gray-700 text-sm">
              {day}
            </div>
          ))}
        </div>

        {/* Days */}
        <div className="grid grid-cols-7">
          {days.map((date, index) => {
            const event = date ? getEventForDay(date) : null;
            const isWorking = date ? isWorkingDay(date) : false;
            const isToday = date && date.toDateString() === new Date().toDateString();

            return (
              <div
                key={index}
                className={`min-h-[100px] border-r border-b border-gray-200 p-2 ${
                  !date ? 'bg-gray-50' : ''
                } ${!isWorking && date ? 'bg-gray-100' : ''} ${
                  isToday ? 'bg-blue-50 ring-2 ring-blue-400 ring-inset' : ''
                } ${event && !event.es_laborable ? 'bg-red-50' : ''}`}
              >
                {date && (
                  <>
                    <div className={`text-sm font-semibold mb-1 ${
                      isToday ? 'text-blue-700' : !isWorking ? 'text-gray-500' : 'text-gray-700'
                    }`}>
                      {date.getDate()}
                    </div>
                    {event && (
                      <div className={`text-xs p-1 rounded ${
                        event.tipo === 'feriado_nacional' ? 'bg-red-100 text-red-700' :
                        event.tipo === 'feriado_regional' ? 'bg-orange-100 text-orange-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        <div className="font-medium">{event.nombre}</div>
                      </div>
                    )}
                    {!isWorking && !event && (
                      <div className="text-xs text-gray-500">No laboral</div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Leyenda */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-700 mb-3">Leyenda:</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-50 border-2 border-blue-400 rounded"></div>
            <span>Hoy</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-50 rounded"></div>
            <span>Feriado</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-100 rounded"></div>
            <span>No laboral</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-white border border-gray-200 rounded"></div>
            <span>Día laboral</span>
          </div>
        </div>
      </div>
    </div>
  );
}
